import { useEffect, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  createWindow,
  deleteWindow,
  listDesiderataForWindow,
  listWindows,
  updateWindow,
} from "@/api/desiderata";
import { listOrganizations } from "@/api/organizations";
import { listPractitioners } from "@/api/practitioners";
import type {
  DesiderataKind,
  DesiderataWindow,
  Practitioner,
} from "@/api/types";
import { usePermission } from "@/auth/usePermission";
import { PageHeader } from "@/components/PageHeader";

const KIND_COLOR: Record<DesiderataKind, "success" | "warning" | "error" | "info"> = {
  WISH: "success",
  AVOID: "warning",
  UNAVAILABLE: "error",
  SWAP_OK: "info",
};

function monthLabel(year: number, month: number, locale: string): string {
  const d = new Date(year, month - 1, 1);
  return d.toLocaleString(locale, { month: "long", year: "numeric" });
}

export function DesiderataAdminPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const canWrite = usePermission("desiderata_window:write");

  const orgs = useQuery({
    queryKey: ["organizations-active"],
    queryFn: () => listOrganizations({ status: "ACTIVE", limit: 100 }),
  });

  const [orgId, setOrgId] = useState<string>("");
  useEffect(() => {
    if (!orgId && orgs.data?.items.length) {
      setOrgId(orgs.data.items[0]?.id ?? "");
    }
  }, [orgs.data, orgId]);

  const windows = useQuery({
    queryKey: ["windows", orgId],
    queryFn: () => (orgId ? listWindows({ organization_id: orgId }) : Promise.resolve([])),
    enabled: Boolean(orgId),
  });

  const [showForm, setShowForm] = useState(false);
  const [viewing, setViewing] = useState<DesiderataWindow | null>(null);

  return (
    <Box>
      <PageHeader
        helpTerm="fenetreDesiderata"
        title={t("desiderata.manageTitle")}
        subtitle={t("desiderata.manageSubtitle")}
        actions={
          canWrite ? (
            <Button startIcon={<AddIcon />} variant="contained" onClick={() => setShowForm(true)}>
              {t("desiderata.createWindow")}
            </Button>
          ) : undefined
        }
      />

      <FormControl size="small" sx={{ minWidth: 280, mb: 2 }}>
        <InputLabel>{t("desiderata.kind.WISH")}…</InputLabel>
        <Select
          label={t("planning.organization")}
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
        >
          {(orgs.data?.items ?? []).map((o) => (
            <MenuItem key={o.id} value={o.id}>
              {o.code} — {o.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {windows.isLoading ? (
        <CircularProgress />
      ) : (windows.data ?? []).length === 0 ? (
        <Alert severity="info">{t("desiderata.noWindow")}</Alert>
      ) : (
        <Stack spacing={1.5}>
          {(windows.data ?? []).map((w) => (
            <WindowCard
              key={w.id}
              window={w}
              locale={i18n.language}
              canWrite={canWrite}
              onView={() => setViewing(w)}
              onLockToggle={() =>
                updateWindowStatus(w.id, w.status === "OPEN" ? "LOCKED" : "OPEN", qc, orgId)
              }
              onDelete={() => {
                if (confirm(t("desiderata.deleteWindowConfirm"))) {
                  deleteWindow(w.id).then(() =>
                    qc.invalidateQueries({ queryKey: ["windows", orgId] }),
                  );
                }
              }}
            />
          ))}
        </Stack>
      )}

      <CreateWindowDialog
        open={showForm}
        onClose={() => setShowForm(false)}
        organizationId={orgId}
        onCreated={() => {
          setShowForm(false);
          qc.invalidateQueries({ queryKey: ["windows", orgId] });
        }}
      />

      <SubmissionsDialog
        open={viewing !== null}
        window={viewing}
        onClose={() => setViewing(null)}
      />
    </Box>
  );
}


function updateWindowStatus(
  id: string,
  newStatus: "OPEN" | "LOCKED",
  qc: ReturnType<typeof useQueryClient>,
  orgId: string,
) {
  updateWindow(id, { status: newStatus }).then(() =>
    qc.invalidateQueries({ queryKey: ["windows", orgId] }),
  );
}


function WindowCard({
  window: w,
  locale,
  canWrite,
  onView,
  onLockToggle,
  onDelete,
}: {
  window: DesiderataWindow;
  locale: string;
  canWrite: boolean;
  onView: () => void;
  onLockToggle: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
              <Typography variant="h6" sx={{ fontWeight: 600, textTransform: "capitalize" }}>
                {monthLabel(w.year, w.month, locale)}
              </Typography>
              <Chip
                size="small"
                color={w.status === "OPEN" ? "success" : w.status === "LOCKED" ? "warning" : "default"}
                label={t(`desiderata.windowStatus.${w.status}`)}
              />
              <Chip size="small" variant="outlined" label={`${w.quota_points} pts max`} />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              {new Date(w.opens_at).toLocaleString(locale)} →{" "}
              {new Date(w.closes_at).toLocaleString(locale)}
            </Typography>
            {w.description && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {w.description}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title={t("desiderata.viewSubmissions")}>
              <IconButton onClick={onView}>
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
            {canWrite && (
              <>
                <Tooltip title={w.status === "OPEN" ? t("desiderata.lock") : t("desiderata.unlock")}>
                  <IconButton onClick={onLockToggle}>
                    {w.status === "OPEN" ? <LockIcon /> : <LockOpenIcon />}
                  </IconButton>
                </Tooltip>
                <Tooltip title={t("desiderata.deleteWindowConfirm")}>
                  <IconButton color="error" onClick={onDelete}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}


function CreateWindowDialog({
  open,
  onClose,
  organizationId,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const now = new Date();
  const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const [year, setYear] = useState(next.getFullYear());
  const [month, setMonth] = useState(next.getMonth() + 1);
  const todayISO = now.toISOString().slice(0, 16);
  const inTwoWeeksISO = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 16);
  const [opensAt, setOpensAt] = useState(todayISO);
  const [closesAt, setClosesAt] = useState(inTwoWeeksISO);
  const [quotaPoints, setQuotaPoints] = useState(20);
  const [description, setDescription] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setServerError(null);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () =>
      createWindow({
        organization_id: organizationId,
        year,
        month,
        opens_at: new Date(opensAt).toISOString(),
        closes_at: new Date(closesAt).toISOString(),
        quota_points: quotaPoints,
        description: description || null,
      }),
    onSuccess: () => onCreated(),
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      setServerError(typeof detail === "string" ? detail : (e as Error).message);
    },
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("desiderata.createWindow")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {serverError && <Alert severity="error">{serverError}</Alert>}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Année"
              type="number"
              inputProps={{ min: 2024, max: 2099 }}
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10) || 2026)}
              size="small"
              sx={{ width: 120 }}
            />
            <FormControl size="small" sx={{ width: 140 }}>
              <InputLabel>Mois</InputLabel>
              <Select
                label="Mois"
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t("desiderata.quotaPoints")}
              type="number"
              inputProps={{ min: 0, max: 100 }}
              value={quotaPoints}
              onChange={(e) => setQuotaPoints(parseInt(e.target.value, 10) || 0)}
              size="small"
              sx={{ width: 140 }}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label={t("desiderata.opensAt")}
              type="datetime-local"
              value={opensAt}
              onChange={(e) => setOpensAt(e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={t("desiderata.closesAt")}
              type="datetime-local"
              value={closesAt}
              onChange={(e) => setClosesAt(e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
          <TextField
            label="Description (optionnel)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
          />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button onClick={onClose}>{t("common.cancel")}</Button>
            <Button
              variant="contained"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending || !organizationId}
            >
              {t("common.save")}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}


function SubmissionsDialog({
  open,
  window: w,
  onClose,
}: {
  open: boolean;
  window: DesiderataWindow | null;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();

  const submissions = useQuery({
    queryKey: ["desiderata-submissions", w?.id],
    queryFn: () => (w ? listDesiderataForWindow(w.id) : Promise.resolve([])),
    enabled: open && Boolean(w),
  });

  const practitioners = useQuery({
    queryKey: ["practitioners-all"],
    queryFn: () => listPractitioners({ limit: 200 }),
    enabled: open,
  });

  const pById = new Map<string, Practitioner>();
  for (const p of practitioners.data?.items ?? []) pById.set(p.id, p);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {t("desiderata.submissions")}
        {w && (
          <Typography variant="body2" color="text.secondary" sx={{ textTransform: "capitalize" }}>
            {monthLabel(w.year, w.month, i18n.language)} ·{" "}
            {submissions.data?.length ?? 0} saisies
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {submissions.isLoading ? (
          <CircularProgress />
        ) : (submissions.data ?? []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        ) : (
          <List dense>
            {(submissions.data ?? []).map((d) => {
              const p = pById.get(d.practitioner_id);
              return (
                <ListItem key={d.id} divider>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 2 }}>
                    <Chip
                      size="small"
                      color={KIND_COLOR[d.kind]}
                      label={t(`desiderata.kind.${d.kind}`)}
                      variant="outlined"
                    />
                    {d.kind !== "UNAVAILABLE" && (
                      <Chip size="small" variant="outlined" label={`${d.weight}`} />
                    )}
                  </Stack>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {p?.display_name ?? d.practitioner_id.slice(0, 8)}
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                          {d.date}
                        </Typography>
                      </Stack>
                    }
                    secondary={d.comment ?? undefined}
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}
